/**
 * @file CoursWOD.tsx
 * @summary Page service
 *
 * Page descriptive d'un service proposé par la box (cours WOD, débutant, hybride, reprise du sport).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { Link } from "react-router-dom";

const URL = "https://crossfitchistera.fr/cours/wod";

const faq = [
  {
    "@type": "Question",
    name: "Combien de temps dure un cours WOD ?",
    acceptedAnswer: { "@type": "Answer", text: "Un cours WOD dure 1 heure : échauffement, partie technique (skill ou force), Metcon chronométré et retour au calme, le tout encadré par un coach diplômé." },
  },
  {
    "@type": "Question",
    name: "Faut-il être en forme pour faire un cours WOD ?",
    acceptedAnswer: { "@type": "Answer", text: "Non. Chaque WOD est scalable : les charges, les mouvements et les volumes sont adaptés à votre niveau, du débutant total au compétiteur. Les deux premières séances sont gratuites." },
  },
  {
    "@type": "Question",
    name: "Combien de personnes par cours WOD chez CrossFit Chistera ?",
    acceptedAnswer: { "@type": "Answer", text: "12 personnes maximum par créneau, pour garantir un encadrement de qualité et une correction technique individuelle par le coach." },
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Cours collectif CrossFit (WOD)",
    name: "Cours WOD CrossFit",
    provider: { "@id": "https://crossfitchistera.fr/#localbusiness" },
    areaServed: { "@type": "City", name: "Saint-Pierre-de-Chandieu" },
    url: URL,
    description: "Cours collectif WOD CrossFit (Workout of the Day) : 1 heure encadrée par un coach diplômé, max 12 personnes, tous niveaux.",
  },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

export default function CoursWOD() {
  return (
    <>
      <SEO
        title="Cours WOD CrossFit (Workout of the Day) | Chistera"
        description="Cours collectif WOD CrossFit (1h, max 12) à Saint-Pierre-de-Chandieu. Coachs diplômés, tous niveaux, 6 jours/7. 2 séances d'essai gratuites."
        canonical={URL}
        jsonLd={jsonLd}
      />
      <ServicePageLayout
        breadcrumbs={[{ label: "Cours" }, { label: "WOD" }]}
        h1="Cours WOD CrossFit — Workout of the Day"
        intro="Le cours WOD est le cœur de l'offre CrossFit Chistera : 1 heure encadrée par un coach diplômé, 12 personnes maximum, tous niveaux. Échauffement, technique, Metcon chronométré, étirements. 6 jours sur 7, à partir de 64 €/mois."
      >
        <h2>Qu'est-ce qu'un cours WOD ?</h2>
        <p>
          Le <strong>WOD</strong> (Workout of the Day, l'« entraînement du jour ») est le cours collectif officiel de la méthode CrossFit. Il combine en une seule séance d'1 heure du <strong>renforcement musculaire</strong>, de la <strong>gymnastique</strong>, de l'<strong>haltérophilie</strong> et du <strong>cardio</strong>, sous la conduite d'un coach diplômé. Chaque séance est différente, programmée pour développer toutes les qualités physiques sur la durée.
        </p>
        <p>
          Chez CrossFit Chistera, la programmation est pensée pour <strong>tous les niveaux</strong> : un débutant, un sportif occasionnel et un compétiteur peuvent s'entraîner ensemble dans le même cours, chacun à sa charge et son intensité. C'est ce qu'on appelle le <em>scaling</em>.
        </p>

        <h2>Le déroulé d'un cours WOD</h2>
        <ol>
          <li><strong>Warm-up (10 min)</strong> — échauffement général puis spécifique aux mouvements du jour.</li>
          <li><strong>Skill ou Strength (15-20 min)</strong> — apprentissage technique d'un mouvement ou travail de force avec charges.</li>
          <li><strong>Metcon (15-25 min)</strong> — la partie chronométrée : AMRAP, EMOM, For Time ou Tabata. C'est l'effort intense du jour.</li>
          <li><strong>Cooldown (5 min)</strong> — retour au calme, mobilité et étirements.</li>
        </ol>

        <h2>Pour qui ?</h2>
        <ul>
          <li><strong>Débutants complets</strong> qui découvrent le sport ou reprennent après une longue pause — voir notre page <Link to="/crossfit-debutant">CrossFit débutant</Link>.</li>
          <li><strong>Adultes en reprise du sport</strong> — voir notre page <Link to="/reprendre-le-sport">reprendre le sport</Link>.</li>
          <li><strong>Sportifs confirmés</strong> qui cherchent une méthode complète et variée.</li>
          <li><strong>Compétiteurs</strong> préparant l'Open CrossFit, Hyrox ou ATHX (cf. <Link to="/cours/hybrid-training">Hybrid Training</Link>).</li>
        </ul>

        <h2>Encadrement et sécurité</h2>
        <p>
          Tous les cours WOD sont encadrés par <strong>Anthony Bendahmane</strong> (Master STAPS, CrossFit Level 1) ou <strong>Cédric Bastien</strong> (BPJEPS, CrossFit Level 1). Le ratio de 12 personnes maximum permet une correction technique individuelle, gage de progression et de sécurité.
        </p>

        <h2>Tarifs et accès</h2>
        <p>
          Les cours WOD sont inclus dans tous les abonnements CrossFit Chistera : 64 €/mois (2 séances/sem), 74 €/mois (3 séances/sem), 90 €/mois (illimité). Drop-in à 15 €. Les <strong>2 premières séances sont gratuites</strong>.
        </p>

        <h2>Questions fréquentes</h2>
        <h3>Combien de temps dure un cours WOD ?</h3>
        <p>1 heure pleine, du warm-up au cooldown.</p>
        <h3>Faut-il être en forme pour commencer ?</h3>
        <p>Non. Tout est scalé à votre niveau. Le coach vous propose des variantes adaptées à chaque exercice.</p>
        <h3>Combien de personnes par cours ?</h3>
        <p>12 maximum, pour conserver un coaching personnalisé.</p>
      </ServicePageLayout>
    </>
  );
}
